import React, { useCallback, useMemo, useState } from "react";
import {
    APIProvider,
    Map,
    AdvancedMarker,
    type ColorScheme,
    type MapProps,
} from "@vis.gl/react-google-maps";
import { buildSatelliteMapOptions, type VisMapOptions } from "./googleMapOptions";

interface GoogleMapProps {
    lat: number | null;
    lng: number | null;

    // same defaults as original
    defaultZoom?: number;
    mapId?: string;

    // allow overrides
    mapOptions?: Partial<VisMapOptions>;
    colorScheme?: ColorScheme;
    mapProps?: Partial<MapProps>;

    // override hooks / rendering if needed
    onApiLoad?: () => void;
    renderLoading?: () => React.ReactNode;
    header?: React.ReactNode;

    children?: React.ReactNode;
}

const DEFAULT_CLASSNAME =
    "maps-container border border-slate-200 rounded-md overflow-hidden relative h-dvh w-full max-h-150";

const DEFAULT_STYLE: React.CSSProperties = {
    width: "100%",
    height: "100%",
};

const DefaultHeader = () => (
    <div>
        <p>
            *The property characteristics and below images here are from a 3rd party
            source and have not been confirmed by Stewart Valuation Intelligence
        </p>
    </div>
);

const DefaultLoading = () => (
    <div
        style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f0f0f0",
        }}
    >
        Loading map...
    </div>
);

export const GoogleMap: React.FC<GoogleMapProps> = ({
                                                        lat,
                                                        lng,
                                                        defaultZoom = 20, // OG default
                                                        mapId = "map-with-satellite", // OG default
                                                        mapOptions,
                                                        colorScheme,
                                                        mapProps,
                                                        onApiLoad,
                                                        renderLoading,
                                                        header,
                                                        children,
                                                    }) => {
    if (lat == null || lng == null) return null;

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    const center = useMemo(() => ({ lat, lng }), [lat, lng]);


    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

    const handleApiLoaded = useCallback(() => {
        setGoogleMapsLoaded(true);
        onApiLoad?.();
    }, [onApiLoad]);

    const options = useMemo(
        () => buildSatelliteMapOptions(mapOptions),
        [mapOptions, googleMapsLoaded]
    );


    return (
        <div className={DEFAULT_CLASSNAME}>
            {header ?? <DefaultHeader />}

            <APIProvider apiKey={apiKey} onLoad={handleApiLoaded}>
                {googleMapsLoaded ? (
                    <Map
                        defaultCenter={center}
                        defaultZoom={defaultZoom}
                        mapId={mapId}
                        style={DEFAULT_STYLE}
                        colorScheme={colorScheme}
                        {...mapProps}      // allow event overrides etc
                        {...options}       // google map behavior options
                    >
                        {children ?? <AdvancedMarker position={center} />}
                    </Map>
                ) : (
                    renderLoading?.() ?? <DefaultLoading />
                )}
            </APIProvider>
        </div>
    );
};